import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClient } from "../../../../../app/context/ClientContext";
import { useAuth } from "../../../../../app/context/AuthContext";
import { useAlert } from "../../../../../app/context/AlertContext";
import { getAllProjectRoles } from "../../../../../core/services/projectRole.service";
import { getAllProjectTypes } from "../../../../../core/services/projectType.service";
import {
  deleteProjectResource,
  downloadProjectFile,
  getFileByProject,
  viewFile,
} from "../../../../../core/services/project.service";
import { getUsersByClientId } from "../../../../../core/services/profile.service";
import {
  buildCreateProjectUsersPayload,
  getAuthenticatedUserFromStorage,
  isAdminUser,
} from "../../../../../core/utils/util";
import { getProjectId } from "../../../utils/projectUtils";
import { sortKeyDatesByDate } from "../../TimelineKickoffFields";
import {
  buildProjectFormFromTarget,
  EMPTY_FORM,
  extractProjectFilesList,
  getUserUsername,
  isCreatorUser,
  isImageFileName,
  loadProfileImageUrl,
  normalizeApiUser,
  normalizeProjectFileEntry,
  parseProjectUsersRoleMap,
  WIZARD_STEPS,
} from "../shared";
import {
  hasProjectKnowledgeXlsxBlockedFile,
  PROJECT_KNOWLEDGE_XLSX_ERROR,
} from "../../../../../core/services/fileUpload.service";

const loadExistingKnowledgeEntry = async (projectId, fileName, index) => {
  const entry = normalizeProjectFileEntry(fileName, index);
  if (!entry) return null;

  if (!isImageFileName(entry.name)) return entry;

  try {
    const result = await viewFile(projectId, entry.name);
    if (result?.mode === "blob" && result.blob instanceof Blob) {
      const treatAsImage =
        isImageFileName(entry.name) ||
        Boolean(result.blob.type?.startsWith("image/"));
      if (treatAsImage && result.blob.size > 0) {
        return {
          ...entry,
          url: URL.createObjectURL(result.blob),
          revokeOnCleanup: true,
        };
      }
    }
  } catch {
    // Keep filename-only entry when preview fetch fails.
  }

  return entry;
};

export default function useCreateProjectModal({
  open,
  onClose,
  onSubmit,
  clientId,
  mode,
  editTarget,
  defaultStep = null,
}) {
  const isEditMode = mode === "edit";
  const { userInfo } = useAuth();
  const { clients } = useClient();
  const { showAlert } = useAlert();
  const isAdmin = isAdminUser(userInfo);
  const creatorUser = useMemo(
    () => getAuthenticatedUserFromStorage() || userInfo,
    [userInfo],
  );
  const editProjectId = isEditMode ? getProjectId(editTarget) : null;

  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectTypesLoading, setProjectTypesLoading] = useState(false);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientProjectsLoading, setClientProjectsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [projectRoles, setProjectRoles] = useState([]);
  const [projectRolesLoading, setProjectRolesLoading] = useState(false);
  const [userAssignments, setUserAssignments] = useState([]);
  const [existingKnowledgeFiles, setExistingKnowledgeFiles] = useState([]);
  const [existingKnowledgeLoading, setExistingKnowledgeLoading] = useState(false);
  const [deletingKnowledgeFileNames, setDeletingKnowledgeFileNames] = useState([]);
  const [downloadingFileNames, setDownloadingFileNames] = useState([]);
  const profileImageUrlsRef = useRef([]);
  const existingObjectUrlsRef = useRef([]);
  const contentRef = useRef(null);

  const canGoPrevious = activeStep > 0;
  const canGoNext = activeStep < WIZARD_STEPS.length - 1;
  const canSubmit = Boolean(form.projectName.trim());

  const handleNextStep = useCallback(() => {
    setActiveStep((step) => Math.min(WIZARD_STEPS.length - 1, step + 1));
  }, []);

  const handlePreviousStep = useCallback(() => {
    setActiveStep((step) => Math.max(0, step - 1));
  }, []);

  const handleStepClick = useCallback((stepIndex) => {
    if (stepIndex < 0 || stepIndex >= WIZARD_STEPS.length) return;
    setActiveStep(stepIndex);
  }, []);

  const handleDialogClose = useCallback((_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
  }, []);

  const revokeProfileImageUrls = useCallback(() => {
    profileImageUrlsRef.current.forEach((imageUrl) => {
      URL.revokeObjectURL(imageUrl);
    });
    profileImageUrlsRef.current = [];
  }, []);

  const revokeExistingObjectUrls = useCallback(() => {
    existingObjectUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Ignore revoke failures.
      }
    });
    existingObjectUrlsRef.current = [];
  }, []);

  const handleDownloadExistingFile = useCallback(
    async (fileName) => {
      const name = String(fileName ?? "").trim();
      if (!editProjectId || !name || downloadingFileNames.includes(name)) return;

      setDownloadingFileNames((prev) =>
        prev.includes(name) ? prev : [...prev, name],
      );

      try {
        await downloadProjectFile(editProjectId, name);
      } catch (err) {
        showAlert("Error", err?.message || "Failed to download file");
      } finally {
        setDownloadingFileNames((prev) => prev.filter((item) => item !== name));
      }
    },
    [downloadingFileNames, editProjectId, showAlert],
  );

  const handleRemoveExistingKnowledgeFile = useCallback(
    async (fileName) => {
      const name = String(fileName ?? "").trim();
      if (
        !editProjectId ||
        !name ||
        deletingKnowledgeFileNames.includes(name)
      ) {
        return;
      }

      setDeletingKnowledgeFileNames((prev) =>
        prev.includes(name) ? prev : [...prev, name],
      );

      try {
        await deleteProjectResource(editProjectId, name);
        setExistingKnowledgeFiles((prev) => {
          const removed = prev.find((entry) => entry.name === name);
          if (removed?.revokeOnCleanup && removed.url) {
            URL.revokeObjectURL(removed.url);
            existingObjectUrlsRef.current = existingObjectUrlsRef.current.filter(
              (url) => url !== removed.url,
            );
          }
          return prev.filter((entry) => entry.name !== name);
        });
      } catch (err) {
        showAlert("Error", err?.message || "Failed to delete file");
      } finally {
        setDeletingKnowledgeFileNames((prev) =>
          prev.filter((item) => item !== name),
        );
      }
    },
    [deletingKnowledgeFileNames, editProjectId, showAlert],
  );

  useEffect(() => {
    if (!open) return;

    setForm(
      isEditMode
        ? buildProjectFormFromTarget(editTarget)
        : buildProjectFormFromTarget(null),
    );
    setError("");
    const stepIndex =
      typeof defaultStep === "number" &&
      defaultStep >= 0 &&
      defaultStep < WIZARD_STEPS.length
        ? defaultStep
        : 0;
    setActiveStep(stepIndex);
    setUserAssignments([]);
    setDeletingKnowledgeFileNames([]);
    setDownloadingFileNames([]);

    if (!isEditMode) {
      revokeExistingObjectUrls();
      setExistingKnowledgeFiles([]);
      setExistingKnowledgeLoading(false);
    }
  }, [open, isEditMode, editTarget, defaultStep, revokeExistingObjectUrls]);

  // Prefill uploaded knowledge-bank files when editing (list + viewFile for image previews).
  useEffect(() => {
    if (!open || !isEditMode || !editProjectId) {
      return undefined;
    }

    let cancelled = false;

    const loadExisting = async () => {
      setExistingKnowledgeLoading(true);
      revokeExistingObjectUrls();

      try {
        const raw = await getFileByProject(editProjectId);
        if (cancelled) return;

        let names = extractProjectFilesList(raw);
        if (!names.length && editTarget) {
          names = extractProjectFilesList(
            editTarget.files ??
              editTarget.knowledgeFiles ??
              editTarget.knowledgeSources ??
              editTarget.documents ??
              editTarget.KnowledgeFiles,
          );
        }

        const objectUrls = [];
        const entries = (
          await Promise.all(
            names.map((fileName, index) =>
              loadExistingKnowledgeEntry(editProjectId, fileName, index),
            ),
          )
        ).filter(Boolean);

        entries.forEach((entry) => {
          if (entry.revokeOnCleanup && entry.url) objectUrls.push(entry.url);
        });

        if (cancelled) {
          objectUrls.forEach((url) => URL.revokeObjectURL(url));
          return;
        }

        existingObjectUrlsRef.current = objectUrls;
        setExistingKnowledgeFiles(entries);
      } catch {
        if (!cancelled) {
          revokeExistingObjectUrls();
          setExistingKnowledgeFiles([]);
        }
      } finally {
        if (!cancelled) setExistingKnowledgeLoading(false);
      }
    };

    loadExisting();

    return () => {
      cancelled = true;
    };
  }, [open, isEditMode, editProjectId, editTarget, revokeExistingObjectUrls]);

  // Prefill Assign Users & Roles when editing an existing project.
  useEffect(() => {
    if (!open || !isEditMode || usersLoading || !allUsers.length) {
      return undefined;
    }

    if (!editProjectId) return undefined;

    const roleByUsername = parseProjectUsersRoleMap(editTarget?.users);
    if (!roleByUsername.size) {
      setUserAssignments([]);
      return undefined;
    }

    const userByUsername = new Map();
    allUsers.forEach((user) => {
      const username = getUserUsername(user).trim().toLowerCase();
      if (username) userByUsername.set(username, user);
    });

    const assignments = [];
    roleByUsername.forEach((roles, username) => {
      const user = userByUsername.get(username);
      if (!user || isCreatorUser(user, creatorUser)) return;
      assignments.push({
        userId: user.uid,
        roles: roles || [],
      });
    });

    setUserAssignments(assignments);
    return undefined;
  }, [
    open,
    isEditMode,
    editTarget,
    editProjectId,
    allUsers,
    usersLoading,
    creatorUser,
  ]);

  const handleSelectedUsersChange = useCallback((selectedUsers) => {
    const filteredSelected = (selectedUsers || []).filter(
      (user) => !isCreatorUser(user, creatorUser),
    );

    setUserAssignments((prev) => {
      const selectedIds = new Set(
        filteredSelected.map((user) => String(user.uid)),
      );
      const kept = prev.filter((entry) => selectedIds.has(String(entry.userId)));
      const keptIds = new Set(kept.map((entry) => String(entry.userId)));
      const added = filteredSelected
        .filter((user) => !keptIds.has(String(user.uid)))
        .map((user) => ({ userId: user.uid, roles: [] }));

      return [...kept, ...added];
    });
  }, [creatorUser]);

  const handleRemoveUser = useCallback((userId) => {
    setUserAssignments((prev) =>
      prev.filter((entry) => String(entry.userId) !== String(userId)),
    );
  }, []);

  const handleUpdateUserRoles = useCallback((userId, roles) => {
    setUserAssignments((prev) =>
      prev.map((entry) =>
        String(entry.userId) === String(userId) ? { ...entry, roles } : entry,
      ),
    );
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const frameId = requestAnimationFrame(() => {
      contentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    });

    return () => cancelAnimationFrame(frameId);
  }, [activeStep, open]);

  useEffect(() => {
    if (!open || !clientId) {
      setClientProjects([]);
      return undefined;
    }

    setClientProjectsLoading(true);

    const client = clients.find(
      (item) =>
        String(item?.clientId ?? item?.Id ?? item?.id ?? "") ===
        String(clientId),
    );
    const list = Array.isArray(client?.projects)
      ? client.projects.filter(
          (project) => !(project?.archived || project?.isArchived),
        )
      : [];

    setClientProjects(list);
    setClientProjectsLoading(false);

    return undefined;
  }, [open, clientId, clients]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setProjectTypesLoading(true);

    getAllProjectTypes()
      .then((list) => {
        if (!cancelled) setProjectTypes(list);
      })
      .catch(() => {
        if (!cancelled) setProjectTypes([]);
      })
      .finally(() => {
        if (!cancelled) setProjectTypesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !clientId) {
      revokeProfileImageUrls();
      setAllUsers([]);
      return undefined;
    }

    let cancelled = false;
    setUsersLoading(true);

    getUsersByClientId(clientId)
      .then(async (usernames) => {
        if (cancelled) return;

        const normalizedUsers = (Array.isArray(usernames) ? usernames : [])
          .map(normalizeApiUser)
          .filter(Boolean)
          .filter((user) => user.enabled !== false);

        const nextProfileImageUrls = [];
        const usersWithProfileImages = await Promise.all(
          normalizedUsers.map(async (user) => {
            const profileImageUrl = await loadProfileImageUrl(user.uid);
            if (profileImageUrl) nextProfileImageUrls.push(profileImageUrl);
            return { ...user, profileImageUrl };
          }),
        );

        if (cancelled) {
          nextProfileImageUrls.forEach((url) => URL.revokeObjectURL(url));
          return;
        }

        revokeProfileImageUrls();
        profileImageUrlsRef.current = nextProfileImageUrls;
        setAllUsers(usersWithProfileImages);
      })
      .catch(() => {
        if (!cancelled) {
          revokeProfileImageUrls();
          setAllUsers([]);
        }
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, clientId, revokeProfileImageUrls]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setProjectRolesLoading(true);

    getAllProjectRoles()
      .then((list) => {
        if (!cancelled) setProjectRoles(list);
      })
      .catch(() => {
        if (!cancelled) setProjectRoles([]);
      })
      .finally(() => {
        if (!cancelled) setProjectRolesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => () => revokeProfileImageUrls(), [revokeProfileImageUrls]);
  useEffect(() => () => revokeExistingObjectUrls(), [revokeExistingObjectUrls]);

  const handleSubmit = useCallback(async () => {
    const projectName = form.projectName.trim();
    const projectTypeName = form.projectTypeName.trim();

    if (!projectName) {
      setError("Project name is required.");
      return;
    }

    if (hasProjectKnowledgeXlsxBlockedFile(form.knowledgeBank)) {
      setError(PROJECT_KNOWLEDGE_XLSX_ERROR);
      return;
    }

    setError("");
    try {
      const users = buildCreateProjectUsersPayload({
        userAssignments,
        allUsers,
        creatorUser,
        projectRoles,
        isAdmin,
      });

      const payload = {
        projectName,
        projectTypeName,
        knowledgeSharingProjectIds: form.knowledgeSharingProjectIds,
        keyDates: sortKeyDatesByDate(form.keyDates),
        knowledgeBank: form.knowledgeBank,
        users,
      };

      if (isEditMode) {
        if (!editProjectId) {
          setError("Project id is missing.");
          return;
        }
        await onSubmit({ ...payload, projectId: editProjectId });
      } else {
        await onSubmit(payload);
      }

      setForm(buildProjectFormFromTarget(null));
      setUserAssignments([]);
      onClose();
    } catch {
      // Keep panel open on error.
    }
  }, [
    allUsers,
    creatorUser,
    editProjectId,
    form,
    isAdmin,
    isEditMode,
    onClose,
    onSubmit,
    projectRoles,
    userAssignments,
  ]);

  return {
    activeStep,
    allUsers,
    canGoNext,
    canGoPrevious,
    canSubmit,
    clientProjects,
    clientProjectsLoading,
    contentRef,
    creatorUser,
    deletingKnowledgeFileNames,
    downloadingFileNames,
    error,
    existingKnowledgeFiles,
    existingKnowledgeLoading,
    form,
    handleDialogClose,
    handleDownloadExistingFile,
    handleNextStep,
    handlePreviousStep,
    handleRemoveExistingKnowledgeFile,
    handleRemoveUser,
    handleSelectedUsersChange,
    handleStepClick,
    handleSubmit,
    handleUpdateUserRoles,
    isAdmin,
    isEditMode,
    projectRoles,
    projectRolesLoading,
    projectTypes,
    projectTypesLoading,
    setForm,
    userAssignments,
    usersLoading,
  };
}
